import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MapComponent } from './map-component';
import { StoreSidebarComponent } from './store-sidebar';
import { MiTienda } from './mi-tienda/mi-tienda';
import { StoreService as StoreDataService } from '../../services/store.service';
import { ActivatedRoute } from '@angular/router';
import { Sucursal } from '../../types/stores';
import { getDataFromResolver } from '../../services/resolvers';
import { Resolvers } from '../../types/Resolvers';
import { ApiMembresiaResponse } from '../../types/API.types';

// Función para calcular la distancia entre dos puntos usando la fórmula de Haversine
function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371; // Radio de la Tierra en kilómetros
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Función para abrir en Google Maps
function openInGoogleMaps(lat: number, lng: number, name: string) {
  const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&destination_place_id=${encodeURIComponent(
    name
  )}`;
  window.open(url, '_blank');
}

// Función para abrir en Waze
function openInWaze(lat: number, lng: number) {
  const url = `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`;
  window.open(url, '_blank');
}

@Component({
  selector: 'allas-store-selector',

  imports: [CommonModule, MapComponent, MiTienda],
  template: `
    <div class="min-h-screen bg-gray-50">
      <!-- Contenedor sin padding adicional -->
      <div>
        <!-- Mensaje de error -->
        <div
          *ngIf="error"
          class="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg lg:absolute lg:top-4 lg:left-4 lg:right-4 lg:z-10 lg:max-w-md"
        >
          <p class="text-red-600 text-sm">{{ error }}</p>
        </div>

        <!-- Layout principal con mapa y sidebar -->
        <div
          class="flex flex-col lg:flex-row gap-6 h-96 md:min-h-[600px] lg:min-h-[calc(100vh-300px)] lg:gap-0 xl:min-h-[calc(100vh-200px)]"
        >
          <!-- Contenedor del mapa -->
          <div
            class="flex-1 bg-white rounded-lg shadow-sm border overflow-hidden relative z-0 lg:rounded-none lg:shadow-none lg:border-0"
          >
            <div
              class="h-96 md:h-[500px] lg:h-[calc(100vh-200px)] relative z-0"
            >
              <allas-map-component
                [stores]="stores"
                [selectedStore]="selectedStore"
                [nearestStore]="nearestStore"
                [userLocation]="userLocation"
                (storeSelect)="handleStoreSelect($event)"
              ></allas-map-component>

              <!-- Componente MiTienda superpuesto del lado IZQUIERDO -->
              <div
                class="absolute top-20 left-4 max-w-[calc(100vw-2rem)] h-[calc(85%-2rem)] z-50 bg-white rounded-lg shadow-lg border overflow-y-auto hidden lg:block"
              >
                <allas-mi-tienda
                  [isMobile]="true"
                  [isFloating]="true"
                  (onClose)="closeMiTienda()"
                  (onStoreSelect)="handleMiTiendaStoreSelect($event)"
                ></allas-mi-tienda>
              </div>
            </div>
          </div>

          <!-- Sidebar para información detallada -->
          <!-- <allas-store-sidebar
            [store]="selectedStore"
            [isOpen]="sidebarOpen"
            [userLocation]="userLocation"
            [isNearestStore]="isSelectedStoreNearest()"
            [isMyStore]="isSelectedStoreMyStore()"
            (close)="closeSidebar()"
            (preferredStore)="handlePreferredStore($event)"
          ></allas-store-sidebar> -->
        </div>

        <!-- Lista compacta de tiendas para mobile -->
        <div class="bg-white rounded-lg overflow-y-auto lg:hidden">
          <allas-mi-tienda
            [isMobile]="true"
            [isFloating]="true"
            (onClose)="closeMiTienda()"
            (onStoreSelect)="handleMiTiendaStoreSelect($event)"
          ></allas-mi-tienda>
        </div>
      </div>
    </div>
  `,
})
export class StoreSelectorComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  storesFromResolver: Sucursal[] =
    getDataFromResolver<ApiMembresiaResponse<Sucursal[]>>(
      Resolvers.STORES,
      this.route
    ).data || [];

  stores = this.storesFromResolver.map((store) => ({
    ...store,
    descripcion: store.name,
    direccion: store.address,
    latitud: store.coordinates.lat,
    longitud: store.coordinates.lng,
  }));

  selectedStore: Sucursal | null = null;
  userLocation: { lat: number; lon: number } | null = null;
  nearestStore: Sucursal | null = null;
  loading = false;
  error: string | null = null;
  sidebarOpen = false;

  constructor(public storeService: StoreDataService) {}

  ngOnInit(): void {
    // Cargar tienda seleccionada previamente
    const savedStore = this.storeService.getSelectedStore();
    if (savedStore) {
      // Verificar que la tienda guardada aún existe en la lista
      const existingStore = this.stores.find(
        (store) =>
          store.descripcion === savedStore.name &&
          store.latitud === savedStore.coordinates.lat &&
          store.longitud === savedStore.coordinates.lng
      );
      if (existingStore) {
        this.selectedStore = existingStore;
      }
    }
  }

  ngOnDestroy(): void {
    // Cleanup si es necesario
  }

  getUserLocation(): void {
    this.loading = true;
    this.error = null;

    if (!navigator.geolocation) {
      this.error = 'La geolocalización no está soportada en este navegador';
      this.loading = false;
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        this.userLocation = { lat: latitude, lon: longitude };

        // Encontrar la tienda más cercana
        let closest = this.stores[0];
        let minDistance = calculateDistance(
          latitude,
          longitude,
          closest.latitud,
          closest.longitud
        );

        this.stores.forEach((store) => {
          const distance = calculateDistance(
            latitude,
            longitude,
            store.latitud,
            store.longitud
          );
          if (distance < minDistance) {
            minDistance = distance;
            closest = store;
          }
        });

        this.nearestStore = closest;
        this.loading = false;
      },
      (error) => {
        this.error = 'No se pudo obtener la ubicación: ' + error.message;
        this.loading = false;
      }
    );
  }

  handleStoreSelect(store: Sucursal): void {
    this.selectedStore = store;
    this.sidebarOpen = true;
  }

  closeSidebar(): void {
    this.sidebarOpen = false;
  }

  handlePreferredStore(store: Sucursal): void {
    // Guardar la tienda como "Mi Tienda" usando el servicio real
    this.storeService.setSelectedStore(store);

    // Mostrar feedback visual
    this.showSuccessMessage(store);
  }

  private showSuccessMessage(store: Sucursal): void {
    // Crear un mensaje temporal de éxito
    const message = document.createElement('div');
    message.className =
      'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-[200] transform transition-all duration-300';
    message.innerHTML = `
      <div class="flex items-center gap-2">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        <span class="font-medium">¡${store.name} establecida como tu tienda!</span>
      </div>
    `;

    document.body.appendChild(message);

    // Remover el mensaje después de 3 segundos
    setTimeout(() => {
      message.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (document.body.contains(message)) {
          document.body.removeChild(message);
        }
      }, 300);
    }, 3000);
  }

  isSelectedStoreNearest(): boolean {
    return (
      this.selectedStore !== null &&
      this.nearestStore !== null &&
      this.selectedStore.name === this.nearestStore.name
    );
  }

  isSelectedStoreMyStore(): boolean {
    const myStore = this.storeService.getSelectedStore();
    return (
      this.selectedStore !== null &&
      myStore !== null &&
      this.selectedStore.name === myStore.name &&
      this.selectedStore.coordinates.lat === myStore.coordinates.lat &&
      this.selectedStore.coordinates.lng === myStore.coordinates.lng
    );
  }

  calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    return calculateDistance(lat1, lon1, lat2, lon2);
  }

  openInGoogleMaps(lat: number, lng: number, name: string): void {
    openInGoogleMaps(lat, lng, name);
  }

  openInWaze(lat: number, lng: number): void {
    openInWaze(lat, lng);
  }

  closeMiTienda(): void {
    // El componente se cierra automáticamente
  }

  handleMiTiendaStoreSelect(store: Sucursal): void {
    this.selectedStore = store;
    // Mostrar mensaje de éxito
    this.showSuccessMessage(store);
  }
}
